import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { useEffect } from 'react';
import Chart from '@/components/item/chart';
import PriceScale from '@/components/item/price-scale';
import Stats from '@/components/item/stats';
import { GetNotificationsButton } from '@/components/notifications';
import { RateLimitPage } from '@/components/rate-limit-page';
import ReviewsSection from '@/components/reviews/reviews-section';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiError, api, fetchApi } from '@/lib/api';
import { computeRichStats } from '@/lib/item-stats';
import { buildCanonicalUrl, buildJsonLd, SITE_DOMAIN } from '@/lib/seo';
import { ssrForPublicOnly } from '@/lib/ssr-utils';
import { urlSanitize } from '@/lib/utils';
import BrandLink from './$name/-components/brand-link';
import BreadCrumbs from './$name/-components/bread-crumbs';
import ImageSwiper from './$name/-components/image-swiper';
import ItemNameChanged from './$name/-components/item-name-changed';
import ItemNotFound from './$name/-components/item-not-found';
import ItemSkeleton from './$name/-components/item-skeleton';
import KspLink from './$name/-components/ksp-link';
import Share from './$name/-components/share';
import Spec from './$name/-components/spec';
import Tags from './$name/-components/tags';
import Title from './$name/-components/title';

export const Route = createFileRoute('/item/$uin/$name')({
  ssr: ssrForPublicOnly,
  loader: async ({ params }) => {
    const { uin, name } = params;
    const uinNumber = Number(uin);

    if (Number.isNaN(uinNumber)) {
      throw notFound();
    }

    try {
      const item = await fetchApi(api.api.items({ uin }).get());

      const sanitizedName = urlSanitize(item.name);

      if (name !== sanitizedName) {
        throw redirect({
          to: '/item/$uin/$name',
          params: {
            uin,
            name: sanitizedName,
          },
          throw: true,
        });
      }

      let reviews: any[] = [];
      try {
        reviews = await fetchApi(api.api.items({ uin }).reviews.get());
      } catch {
        reviews = [];
      }

      const richStats = computeRichStats(
        item.prices.filter((p) => p.price !== null) as Array<{
          date: Date;
          price: number;
          listPrice: number | null;
        }>,
      );

      const title = generateItemSEOTitle(item);
      const description = generateItemSEODescription(item);

      return {
        item,
        richStats: {
          ...richStats,
          meanPrice: item.stats.meanPrice,
        },
        reviews,
        seo: {
          title,
          description,
        },
      };
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        return { rateLimited: true as const };
      }
      if (error instanceof ApiError && error.status === 404) {
        throw notFound();
      }
      throw error;
    }
  },
  head: ({ loaderData }) => {
    const item = loaderData?.item;
    const seo = loaderData?.seo;
    const uin = item?.uin;
    const itemName = item?.name;

    const canonicalPath = uin && itemName ? `/item/${uin}/${urlSanitize(itemName)}` : '/';
    const canonicalUrl = buildCanonicalUrl(canonicalPath);

    const ogImage =
      item?.images && (item.images as any[]).length > 0
        ? String((item.images as any[])[0])
        : `https://${SITE_DOMAIN}/og-default.png`;

    const prices = (item?.prices as any[] | undefined) ?? [];
    const currentPrice = prices.at(-1)?.price ?? 0;

    // Breadcrumb data from itemTags (mirrors logic in bread-crumbs.tsx)
    const itemTags = (item?.itemTags as any[] | undefined) ?? [];
    const cat = itemTags.find((t: any) => t.keyTags?.id === 215)?.valueTags;
    const subCat = itemTags.find((t: any) => t.keyTags?.id === 1)?.valueTags;

    const breadcrumbItems: Array<{
      position: number;
      name: string;
      item?: string;
    }> = [{ position: 1, name: 'Pricedrop', item: `https://${SITE_DOMAIN}/` }];
    if (cat) {
      breadcrumbItems.push({
        position: 2,
        name: cat.name,
        item: `https://${SITE_DOMAIN}/items?filters=${cat.id}`,
      });
    }
    if (subCat) {
      breadcrumbItems.push({
        position: breadcrumbItems.length + 1,
        name: subCat.name,
        item: `https://${SITE_DOMAIN}/items?filters=${subCat.id}`,
      });
    }
    breadcrumbItems.push({
      position: breadcrumbItems.length + 1,
      name: itemName ?? '',
      item: canonicalUrl,
    });

    return {
      meta: [
        {
          title: seo?.title || 'מוצר',
        },
        {
          name: 'description',
          content: seo?.description || 'Pricedrop - מעקב היסטוריית מחירים ב-KSP',
        },
        {
          property: 'og:title',
          content: seo?.title || 'מוצר',
        },
        {
          property: 'og:description',
          content: seo?.description || 'Pricedrop - מעקב היסטוריית מחירים ב-KSP',
        },
        {
          property: 'og:type',
          content: 'product',
        },
        {
          property: 'og:image',
          content: ogImage,
        },
        {
          property: 'og:image:width',
          content: '1200',
        },
        {
          property: 'og:image:height',
          content: '630',
        },
        {
          property: 'og:url',
          content: canonicalUrl,
        },
        {
          property: 'og:site_name',
          content: 'Pricedrop',
        },
        {
          property: 'og:locale',
          content: 'he_IL',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: seo?.title || 'מוצר',
        },
        {
          name: 'twitter:description',
          content: seo?.description || 'Pricedrop - מעקב היסטוריית מחירים ב-KSP',
        },
        {
          name: 'twitter:image',
          content: ogImage,
        },
      ],
      links: [{ rel: 'canonical', href: canonicalUrl }],
      scripts: [
        buildJsonLd({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: itemName ?? '',
          description: seo?.description ?? '',
          image: ogImage,
          ...(item?.brand?.name ? { brand: { '@type': 'Brand', name: item.brand.name } } : {}),
          offers: {
            '@type': 'Offer',
            url: canonicalUrl,
            priceCurrency: 'ILS',
            price: String(currentPrice),
            availability:
              currentPrice > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          },
        }),
        buildJsonLd({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((bc) => ({
            '@type': 'ListItem',
            position: bc.position,
            name: bc.name,
            ...(bc.item ? { item: bc.item } : {}),
          })),
        }),
      ],
    };
  },
  pendingComponent: ItemSkeleton,
  notFoundComponent: ItemNotFound,
  component: ItemPage,
});

function generateItemSEOTitle(item: any): string {
  const parts: string[] = [];
  parts.push(item.name);

  const brandName = item.brand?.name;
  if (brandName) {
    parts.push(brandName);
  }

  parts.push('Pricedrop - מעקב היסטוריית מחירים ב-KSP');

  const title = parts.join(' | ');
  return title.length > 60 ? `${title.substring(0, 57)}...` : title;
}

function generateItemSEODescription(item: any): string {
  const prefix = 'Pricedrop - מעקב היסטוריית מחירים ב-KSP';
  const parts: string[] = [];

  if (item.description && item.description.trim()) {
    const cleanDescription = item.description.replace(/<[^>]*>/g, '').trim();
    parts.push(
      cleanDescription.length > 100 ? `${cleanDescription.substring(0, 97)}...` : cleanDescription,
    );
  }

  if (item.itemTags && item.itemTags.length > 0) {
    const tagNames = item.itemTags.slice(0, 3).map((t: any) => t.valueTags?.name ?? t.tag?.name);
    parts.push(tagNames.join(', '));
  }

  const suffix = parts.join(' - ');
  const description = suffix ? `${prefix} | ${suffix}` : prefix;
  return description.length > 160 ? `${description.substring(0, 157)}...` : description;
}

function ItemPage() {
  const loaderData = Route.useLoaderData();
  if (loaderData?.rateLimited) {
    return <RateLimitPage />;
  }
  return <ItemPageContent />;
}

function ItemPageContent() {
  const loaderData = Route.useLoaderData();
  const { item, richStats, reviews } = loaderData as Exclude<
    typeof loaderData,
    { rateLimited: true }
  >;

  const currentPrice = item.prices?.at(-1)?.price ?? null;

  useEffect(() => {
    if (!item.uin) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [item.uin]);

  return (
    <div className="px-3">
      <div className="flex min-h-7 flex-col items-start space-x-3 sm:flex-row sm:items-center">
        <BreadCrumbs itemTags={item.itemTags} uinsql={item.uinsql ?? ''} />
        {item.itemNames && item.itemNames.length > 1 && <ItemNameChanged names={item.itemNames} />}
      </div>

      <Title name={item.name} uinsql={item.uinsql ?? ''} price={currentPrice} />

      <div className="flex flex-col justify-between sm:flex-row sm:gap-3">
        <div className="sm:w-[75%]">
          <Chart
            prices={item.prices.map(
              (p: { date: Date; price: number | null; listPrice: number | null }) => ({
                date: String(p.date),
                price: p.price,
                listPrice: p.listPrice ?? undefined,
              }),
            )}
            stats={{
              max: richStats.max,
              min: richStats.min,
              meanPrice: richStats.meanPrice,
              current: richStats.current,
            }}
          />

          <div className="flex flex-col gap-4">
            <div className="my-2 flex w-full flex-col-reverse gap-4 md:flex-row">
              {currentPrice && currentPrice > 0 && <KspLink uin={item.uin} />}
              <div className="flex w-full flex-auto flex-col md:w-[65%]">
                <GetNotificationsButton
                  itemUin={item.uin}
                  itemName={item.name}
                  currentPrice={currentPrice}
                  isInStock={currentPrice !== null && currentPrice > 0}
                />
              </div>
            </div>

            <div className="flex items-top gap-2">
              <div className="bg-gray-0 w-full rounded border border-gray-200 px-6 py-3 text-right text-gray-700 dark:border-gray-600 dark:text-gray-200">
                {item.description}
              </div>
            </div>

            <div className="mt-1 flex flex-col sm:flex-row">
              <Tags itemTags={item.itemTags} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:w-[25%] sm:flex-col-reverse sm:justify-end">
          <Stats
            prices={item.prices}
            current={richStats.current}
            startDate={richStats.startDate}
            max={richStats.max}
            min={richStats.min}
            meanPrice={richStats.meanPrice}
          />
          <Share />
          <ImageSwiper
            uin={String(item.uin)}
            images={(item.images ?? []).map(String)}
            name={item.name}
          />
          {item.brand && item.brandId && <BrandLink id={item.brandId} name={item.brand.name} />}
        </div>
      </div>

      {richStats.max.price !== richStats.min.price && (
        <div className="my-2">
          <PriceScale
            meanPrice={richStats.meanPrice}
            current={richStats.current}
            min={richStats.min}
            max={richStats.max}
          />
        </div>
      )}

      <div className="mt-6">
        <Tabs defaultValue="reviews" className="w-full">
          <TabsList className="mb-4 grid w-full grid-cols-2 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
            <TabsTrigger
              value="reviews"
              className="min-w-0 flex-1 overflow-hidden text-ellipsis rounded-md px-2 py-1.5 text-xs font-medium text-right transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm sm:px-3 sm:py-2 sm:text-sm md:text-base dark:data-[state=active]:bg-gray-700"
            >
              <span className="truncate">ביקורות ({reviews.length})</span>
            </TabsTrigger>
            <TabsTrigger
              value="specs"
              className="min-w-0 flex-1 overflow-hidden text-ellipsis rounded-md px-2 py-1.5 text-xs font-medium text-right transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm sm:px-3 sm:py-2 sm:text-sm md:text-base dark:data-[state=active]:bg-gray-700"
            >
              <span className="truncate">מפרט טכני</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews" className="mt-4">
            <ReviewsSection itemUin={item.uin} itemName={item.name} initialReviews={reviews} />
          </TabsContent>

          <TabsContent value="specs" className="mt-4">
            {item.itemSpecs && item.itemSpecs.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                <div className="p-6">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                    מפרט טכני - {item.name}
                  </h3>
                  <Spec
                    specs={item.itemSpecs.map((s: { key: string; value: string | null }) => ({
                      key: s.key,
                      value: s.value ?? '',
                    }))}
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white p-6 text-center dark:border-gray-700 dark:bg-gray-800">
                <div className="text-gray-500 dark:text-gray-400">
                  <svg
                    className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <title>no technical specification</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                    אין מפרט טכני זמין
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    מפרט טכני עבור מוצר זה אינו זמין כרגע
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
